import { Injectable, RendererFactory2, inject } from '@angular/core';

import { ModalGestureDebounceUtil } from './modal-gesture-debounce.util';
import { ModalGestureHelperService } from './modal-gesture-helper.service';
import { Background } from './modal-gesture.model';

export type TransformOrigin = { headerTransformOrigin: string; mainColumnTransformOrigin: string };

type DefaultBackgroundElements = { header: HTMLElement; main: HTMLElement };

type IntersectionPoint = { intersectionX: number; intersectionY: number };

@Injectable({ providedIn: 'root' })
export class ModalGestureTransformOriginService {
    readonly #debounceUtil = new ModalGestureDebounceUtil();
    readonly #modalGestureHelper = inject(ModalGestureHelperService);
    readonly #renderer = inject(RendererFactory2).createRenderer(null, null);

    updateTransformOrigin(background: Background): void {
        if (!this.#debounceUtil.isDebounced()) return;

        const defaultBackgroundElements = this.getElementsFromDefaultBackground(background);

        if (!defaultBackgroundElements) return;

        this.setTransformOrigin(defaultBackgroundElements);
    }

    private getElementsFromDefaultBackground(background: Background): DefaultBackgroundElements | null {
        if (background.type !== 'default') return null;

        const header = background.elements[0];
        const main = background.elements[1];

        if (!header || !main) {
            return null;
        }

        return { header, main };
    }

    private setTransformOrigin(defaultBackgroundElements: DefaultBackgroundElements): void {
        const { intersectionX, intersectionY } = this.getIntersectionPoint(defaultBackgroundElements);

        const transformOrigin = { header: `${intersectionX}px bottom`, main: `${intersectionX}px ${intersectionY}px` };

        this.#renderer.setStyle(defaultBackgroundElements.header, 'transform-origin', transformOrigin.header);
        this.#renderer.setStyle(defaultBackgroundElements.main, 'transform-origin', transformOrigin.main);
    }

    // This may be updated to an intersection observer implementation in future
    private getIntersectionPoint({ header, main }: DefaultBackgroundElements): IntersectionPoint {
        const headerRect = header.getBoundingClientRect();
        const mainColumnRect = main.getBoundingClientRect();
        const intersectionX = this.#modalGestureHelper.viewportWidth / 2;
        const intersectionY = mainColumnRect.top * -1 + headerRect.bottom;

        return { intersectionX, intersectionY };
    }
}
